import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface EnergyPanelProps {
  /** Icon rendered at the start of the header (e.g. a lucide icon). */
  icon: ReactNode;
  /** Header title shown beside the icon. */
  title: string;
  /**
   * Optional content rendered directly under the title (e.g. the coloured kWh
   * totals row in EnergyFlowChart).
   */
  headerDetail?: ReactNode;
  /** Optional right-aligned header content. */
  headerRight?: ReactNode;
  /**
   * Optional legend row rendered under the header — typically a set of coloured
   * dots + labels. Matches the EnergyFlowChart legend style/spacing.
   */
  legend?: ReactNode;
  /** Panel body (chart, empty state, etc.). */
  children: ReactNode;
  className?: string;
}

/** A coloured legend / total dot, shared across the energy panels. */
export function EnergyPanelDot({ color }: { color: string }) {
  return (
    <span
      aria-hidden
      className="inline-block size-2 shrink-0 rounded-full"
      style={{ backgroundColor: color }}
    />
  );
}

/**
 * Shared dark "hero" panel for the Energy tab, extracted from the HomeWizard-
 * style EnergyFlowChart so the energy charts read as siblings: a dark rounded
 * card with consistent padding, an icon + title header (with optional detail
 * below the title and optional right-aligned content), and an optional legend
 * row.
 *
 * Intentionally dark in BOTH app themes: this is a premium dark panel, so it
 * keeps a near-black background and light-on-dark text regardless of the
 * ambient theme — matching EnergyFlowChart.
 */
export function EnergyPanel({
  icon,
  title,
  headerDetail,
  headerRight,
  legend,
  children,
  className,
}: EnergyPanelProps) {
  return (
    <div
      data-slot="energy-panel"
      className={cn(
        "flex flex-col gap-4 rounded-3xl bg-zinc-950 p-5 text-zinc-100 shadow-xl ring-1 ring-white/5",
        className,
      )}
    >
      {/* Header: icon + (title above optional detail) on the left, optional
          right-aligned content on the right. */}
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="flex items-center gap-3">
          <span className="text-zinc-400 [&>svg]:size-5" aria-hidden>
            {icon}
          </span>
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-zinc-100">{title}</span>
            {headerDetail}
          </div>
        </div>
        {headerRight}
      </div>

      {/* Legend: coloured dots matching the panel's series. */}
      {legend && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-zinc-400">
          {legend}
        </div>
      )}

      {children}
    </div>
  );
}
